import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ImportedMeme } from "../types";
import MemeTemplatePicker from "../components/MemeTemplatePicker";
import { supabase } from "../utils/supabase";
import { usePlausible } from "../hooks/usePlausible";

const HomePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [userName, setUserName] = useState("");
  const [nameError, setNameError] = useState("");
  const { trackNamePicked } = usePlausible();

  const showTemplatePicker = searchParams.get("showTemplatePicker") === "true";

  const handleImportedMemeSelect = (meme: ImportedMeme) => {
    // Navigate to editor with the meme as template and user name
    const currentName = userName || searchParams.get("name") || "";

    // Check if this is a custom template by looking for the isCustom property
    // We need to cast to any to access the isCustom property that's added in useCombinedMemes
    const isCustomTemplate = (meme as ImportedMeme & { isCustom?: boolean })
      .isCustom;

    // Also check if the category is 'oz_and_tali' as a fallback
    const isCustomByCategory = meme.category === "oz_and_tali";
    const finalIsCustom = isCustomTemplate || isCustomByCategory;

    if (finalIsCustom) {
      // For custom templates, use the template parameter
      navigate(
        `/editor?template=${meme.id}&creator=${encodeURIComponent(currentName)}`
      );
    } else {
      // For imported memes, use the imported_meme parameter
      navigate(
        `/editor?imported_meme=${meme.id}&creator=${encodeURIComponent(
          currentName
        )}`
      );
    }
  };

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userName.trim()) {
      setNameError("אנא הכנס את השם המלא שלך");
      return;
    }
    if (userName.trim().length < 2) {
      setNameError("השם חייב להכיל לפחות 2 תווים");
      return;
    }
    setNameError("");

    const trimmedName = userName.trim();

    // Log user to database
    if (supabase) {
      try {
        // Try to insert the user, or update last_seen_at if they already exist
        await supabase.from("app_users").upsert(
          {
            name: trimmedName,
            last_seen_at: new Date().toISOString(),
          },
          {
            onConflict: "name",
            ignoreDuplicates: false,
          }
        );
      } catch (error) {
        console.error("Error logging user:", error);
        // Don't block the user flow if logging fails
      }
    }

    // Save the name to localStorage
    localStorage.setItem("weddingMemeUserName", trimmedName);

    // Track the name picked event
    trackNamePicked(trimmedName);

    setSearchParams({ showTemplatePicker: "true", name: trimmedName });
  };

  useEffect(() => {
    // Initialize userName from localStorage first, then from URL if it exists
    const savedName = localStorage.getItem("weddingMemeUserName");
    const nameFromUrl = searchParams.get("name");

    if (savedName) {
      setUserName(savedName);
    } else if (nameFromUrl) {
      setUserName(nameFromUrl);
    }
  }, [searchParams]);

  if (showTemplatePicker) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
        {/* Template Picker */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 shadow-xl border border-pink-100">
          <MemeTemplatePicker onSelectTemplate={handleImportedMemeSelect} />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
      {/* Hebrew Explanation */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-2 sm:p-8 md:p-10 lg:p-12 shadow-xl border border-pink-100 -mt-6 mb-2">
        <p
          className="text-md text-gray-700 leading-relaxed text-center"
          dir="rtl"
        >
          <Link to="/gallery" className="text-pink-600 hover:text-pink-700">
            רוצים לראות את המימז ששאר האורחים הכינו?
          </Link>
        </p>
      </div>

      <div className="bg-white/70 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 lg:p-12 shadow-xl border border-pink-100 mb-8">
        <div className="text-center">
          <img
            src="/oz_and_tali.png"
            alt="Logo"
            className="h-48 mx-auto mb-4"
          />
          <h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6"
            dir="rtl"
          >
            בואו להכין לנו מימז לחתונה!
          </h2>
          <div
            className="text-lg sm:text-xl text-gray-700 leading-relaxed"
            dir="rtl"
          >
            <p className="mb-4">
              הרעיון פשוט - אתם מכינים מימז עלינו, אנחנו מדפיסים ותולים אותם
              בחתונה.
            </p>
            <p className="mb-4">
              (תזכרו שסבתא גם באה - קיפ איט פמילי פרנדלי ❤️)
            </p>
          </div>
        </div>
        <hr className="my-4" />
        <form onSubmit={handleNameSubmit} className="max-w-md mx-auto">
          <div className="text-center mb-6">
            <h3
              className="text-xl sm:text-2xl font-bold text-gray-800 mb-2"
              dir="rtl"
            >
              יאללה - תגידו מי מכין את המימ ובואו נרוץ
            </h3>
          </div>

          <div className="mb-6">
            <input
              type="text"
              value={userName}
              onChange={(e) => {
                setUserName(e.target.value);
                setNameError("");
              }}
              placeholder="השם המלא שלכם"
              className={`w-full px-4 py-3 text-lg border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-center ${
                nameError ? "border-red-500" : "border-gray-300"
              }`}
              dir="rtl"
            />
            {nameError && (
              <p className="text-red-500 text-sm mt-2 text-center" dir="rtl">
                {nameError}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="w-full px-6 py-3 bg-gradient-to-r from-pink-600 to-yellow-600 text-white font-bold text-lg rounded-lg hover:from-pink-700 hover:to-yellow-700 transition-all duration-200 transform hover:scale-105"
          >
            יאללה בלאגן
          </button>
        </form>
      </div>
    </div>
  );
};

export default HomePage;
